<template>
  <div class="voice-wrap" :class="{ on: listening, disabled: !supported || disabled }">
    <button
      class="pill mic"
      type="button"
      :disabled="disabled || !supported"
      @click="toggle"
      :title="supported ? (listening ? 'Stop listening' : 'Start listening') : 'Voice not supported in this browser'"
    >
      <span class="dot" aria-hidden="true"></span>
      <span>{{ listening ? "Listening…" : "Mic" }}</span>
    </button>

    <button
      v-if="listening"
      class="pill ghost"
      type="button"
      :disabled="disabled"
      @click="stop"
    >
      Stop
    </button>

    <div v-if="showTranscript && (partialText || lastFinal)" class="voice-transcript">
      <div v-if="partialText" class="partial">“{{ partialText }}”</div>
      <div v-else class="final">Heard: “{{ lastFinal }}”</div>
    </div>
  </div>
</template>

<script>
export default {
  name: "VoiceInput",
  emits: ["final", "partial", "error", "state"],

  props: {
    disabled: { type: Boolean, default: false },
    lang: { type: String, default: "en-US" },
    autoStopOnFinal: { type: Boolean, default: true },
    showTranscript: { type: Boolean, default: true },
  },

  data() {
    return {
      supported: false,
      listening: false,
      partialText: "",
      lastFinal: "",
      rec: null,
    };
  },

  mounted() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.supported = !!SR;

    if (!this.supported) {
      this.$emit("state", { supported: false, listening: false });
      return;
    }

    const rec = new SR();
    rec.lang = this.lang;
    rec.interimResults = true;
    rec.continuous = true;

    rec.onstart = () => {
      this.listening = true;
      this.partialText = "";
      this.$emit("state", { supported: true, listening: true });
    };

    rec.onend = () => {
      this.listening = false;
      this.partialText = "";
      this.$emit("state", { supported: true, listening: false });
    };

    rec.onerror = (e) => {
      this.$emit("error", e);
      // Some browsers end recognition on error automatically.
    };

    rec.onresult = (event) => {
      let interim = "";
      let finalText = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        const txt = res[0]?.transcript || "";
        if (res.isFinal) finalText += txt;
        else interim += txt;
      }

      interim = String(interim || "").trim();
      finalText = String(finalText || "").trim();

      if (interim) {
        this.partialText = interim;
        this.$emit("partial", interim);
      }

      if (finalText) {
        this.lastFinal = finalText;
        this.partialText = "";
        this.$emit("final", finalText);

        if (this.autoStopOnFinal) {
          // stop soon after final so mobile doesn’t keep recording
          setTimeout(() => this.stop(), 50);
        }
      }
    };

    this.rec = rec;
    this.$emit("state", { supported: true, listening: false });
  },

  beforeUnmount() {
    try {
      this.stop();
    } catch (e) {
        // no-op: stop() can throw in some browsers if not active
        }
    this.rec = null;
  },

  methods: {
    start() {
      if (!this.rec || this.disabled) return;
      try {
        this.rec.start();
      } catch (e) {
        // "start" can throw if called twice quickly
        this.$emit("error", e);
      }
    },
    stop() {
      if (!this.rec) return;
      try {
        this.rec.stop();
      } catch (e) {
        // no-op: stop() can throw in some browsers if not active}
      }
    },
    toggle() {
      if (!this.supported || this.disabled) return;
      if (this.listening) this.stop();
      else this.start();
    },
  },
};
</script>

<style scoped>
.voice-wrap {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 10px;
}

.voice-transcript {
  width: 100%;
  font-size: 0.95rem;
  opacity: 0.9;
}

.partial {
  opacity: 0.8;
}

.final {
  opacity: 0.9;
}

.pill.mic {
  display: inline-flex;
  gap: 8px;
  align-items: center;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: currentColor;
  opacity: 0.6;
}

.voice-wrap.on .dot {
  opacity: 1;
}
</style>
